/**
 * Prompts still awaiting the viewer's answer.
 *
 * The server re-validates each one against the live visit / preference / proposal rows,
 * so an answered prompt disappears on the next fetch and this component filters nothing.
 *
 * What each prompt LOOKS like is the surface's business — accepting a time proposal
 * opens a card with two buttons in the member portal and links into a visits tab in the
 * office — so the bar renders the list and delegates each row through
 * `renderPromptBlock`. With no renderer supplied it renders nothing rather than a
 * half-useful default.
 */

import { cn } from "cn";
import { isEmpty } from "lodash-es";
import type { Channel, ChannelPromptBlock } from "../_api/channel-endpoints";
import type { ChannelPromptContext } from "../_types/channel-surface";

type ChannelPromptBlockBarProps = {
  channel: Channel;
  prompts: ChannelPromptBlock[] | undefined;
  renderPromptBlock:
    | ((context: ChannelPromptContext) => React.ReactNode)
    | undefined;
  className?: string;
};

export function ChannelPromptBlockBar({
  channel,
  prompts,
  renderPromptBlock,
  className,
}: ChannelPromptBlockBarProps) {
  if (!renderPromptBlock || isEmpty(prompts)) return null;

  return (
    <div
      data-test="channel-prompt-blocks"
      className={cn(
        /* sorted */
        "flex gap-3",
        "overflow-x-auto",
        "p-3 xl:px-4",
        "border-b border-border-dark-min",
        className,
      )}
    >
      {prompts?.map((prompt) => (
        <div key={prompt.id} className="shrink-0">
          {renderPromptBlock({ prompt, channel })}
        </div>
      ))}
    </div>
  );
}
